import { Clipboard } from '@angular/cdk/clipboard';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    Input,
    OnChanges,
    OnDestroy,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import { marker } from '@common/translate-marker';
import { TooltipDirective } from '@progress/kendo-angular-tooltip';

const COPIED_STATE_DURATION_MS = 2000;
const COPY_ICON_DELAY_MS = 200;

/**
 * Renders a text value truncated to fit its container (single line by default, or clamped to
 * `lines` when set), and — only when the rendered text is actually overflowing — a small action
 * button that reveals the complete value in a tooltip on hover/focus and copies it on click.
 * Truncation is presentation only: the tooltip and clipboard always use the untruncated `value`.
 */
@Component({
    selector: 'app-overflow-text',
    templateUrl: './overflow-text.component.html',
    styleUrls: ['./overflow-text.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class OverflowTextComponent implements AfterViewInit, OnChanges, OnDestroy {
    @Input() value: string | null | undefined;
    @Input() lines = 1;

    @ViewChild('textEl', { static: true }) private textEl: ElementRef<HTMLElement>;
    @ViewChild('actionBtn') private actionBtn: ElementRef<HTMLElement>;

    isOverflowing = false;
    isActive = false;
    isCopied = false;
    showCopyIcon = false;

    private readonly moreLabel = marker('Show full value');
    private readonly copyLabel = marker('Copy to clipboard');
    private readonly copiedLabel = marker('Copied to clipboard');

    private resizeObserver?: ResizeObserver;
    // Typed `any`: this project's global `setTimeout`/`clearTimeout` resolve ambiguously between
    // the DOM (`number`) and @types/node (`NodeJS.Timeout`) overloads depending on build context.
    private copiedTimeout?: any;
    private overflowCheckTimeout?: any;
    private copyIconTimeout?: any;

    constructor(private clipboard: Clipboard) {}

    get actionAriaLabel(): string {
        return this.isCopied ? this.copiedLabel : this.isActive ? this.copyLabel : this.moreLabel;
    }

    ngAfterViewInit(): void {
        if (typeof ResizeObserver !== 'undefined') {
            this.resizeObserver = new ResizeObserver(() => this.checkOverflow());
            this.resizeObserver.observe(this.textEl.nativeElement);
        }
        this.scheduleOverflowCheck();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.value || changes.lines) {
            this.scheduleOverflowCheck();
        }
    }

    ngOnDestroy(): void {
        this.resizeObserver?.disconnect();
        clearTimeout(this.copiedTimeout);
        clearTimeout(this.overflowCheckTimeout);
        clearTimeout(this.copyIconTimeout);
    }

    onEnter(tooltip: TooltipDirective): void {
        this.isActive = true;
        tooltip.toggle(this.actionBtn.nativeElement, true);
        this.scheduleCopyIcon();
    }

    onLeave(tooltip: TooltipDirective): void {
        this.isActive = false;
        tooltip.toggle(this.actionBtn.nativeElement, false);
        this.resetCopyIcon();
    }

    onFocus(tooltip: TooltipDirective): void {
        this.isActive = true;
        tooltip.toggle(this.actionBtn.nativeElement, true);
        this.scheduleCopyIcon();
    }

    onBlur(tooltip: TooltipDirective): void {
        this.isActive = false;
        tooltip.toggle(this.actionBtn.nativeElement, false);
        this.resetCopyIcon();
    }

    onActionClick(event: Event, tooltip: TooltipDirective): void {
        event.stopPropagation();

        if (!this.value) {
            return;
        }

        this.clipboard.copy(this.value);
        tooltip.toggle(this.actionBtn.nativeElement, false);
        this.resetCopyIcon();

        this.isCopied = true;
        clearTimeout(this.copiedTimeout);
        this.copiedTimeout = setTimeout(() => {
            this.isCopied = false;
        }, COPIED_STATE_DURATION_MS);
    }

    // The tooltip shows immediately; the resting icon only turns into the copy icon after a short
    // pause so the icon doesn't flicker while the user is just moving across the cell.
    private scheduleCopyIcon(): void {
        if (this.showCopyIcon || this.copyIconTimeout) {
            return;
        }

        this.copyIconTimeout = setTimeout(() => {
            this.copyIconTimeout = undefined;
            this.showCopyIcon = true;
        }, COPY_ICON_DELAY_MS);
    }

    private resetCopyIcon(): void {
        clearTimeout(this.copyIconTimeout);
        this.copyIconTimeout = undefined;
        this.showCopyIcon = false;
    }

    private scheduleOverflowCheck(): void {
        clearTimeout(this.overflowCheckTimeout);
        // Deferred so the DOM has picked up the latest `value`/`lines` before we measure it,
        // and so this runs outside the change-detection cycle that triggered it.
        this.overflowCheckTimeout = setTimeout(() => this.checkOverflow());
    }

    private checkOverflow(): void {
        const el = this.textEl?.nativeElement;
        if (!el) {
            return;
        }

        this.isOverflowing =
            this.lines > 1 ? el.scrollHeight > el.clientHeight + 1 : el.scrollWidth > el.clientWidth + 1;
    }
}
